import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type DragEvent } from 'react';

import {
  listProductImages,
  removeProductImage,
  reorderProductImages,
  setMainProductImage,
  uploadProductImage,
} from '../../api/business/endpoints';
import type { GalleryImage, ProductDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { MAX_GALLERY_PHOTOS, MAX_PHOTO_BYTES, PHOTO_ACCEPT, moveBy, moveTo } from '../../components/gallery';
import { useContentLanguages } from '../../components/useContentLanguages';
import { variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

import { GalleryImageDialog } from './GalleryImageDialog';

type Upload = {
  key: string;
  name: string;
  /** 0…1 of the bytes sent. */
  progress: number;
  state: 'waiting' | 'sending' | 'done' | 'refused';
  /** Why it was refused, in the user's language. */
  error: string | null;
};

type GalleryDialog =
  | { kind: 'edit'; photo: GalleryImage }
  | { kind: 'remove'; photo: GalleryImage }
  | { kind: 'view'; index: number };

type ProductGallerySectionProps = {
  companyId: string;
  product: ProductDetail;
  /** The user may change the catalogue and the product is active. */
  canManage: boolean;
};

const INVALIDATES = ['product-images', 'product', 'products'];

/**
 * A product's photos (R0032): dropped or picked — several at once, each with its own progress and, when
 * refused, its own reason — in an order changed by dragging or by the arrow buttons, one of them the main
 * photo, each with an alt text per content language and, on a product with variants, the variants it shows.
 */
export const ProductGallerySection = ({ companyId, product, canManage }: ProductGallerySectionProps) => {
  const { t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const input = useRef<HTMLInputElement>(null);
  const query = useQuery({
    queryKey: ['product-images', companyId, product.id],
    queryFn: () => listProductImages(companyId, product.id),
  });
  const [uploads, setUploads] = useState<Array<Upload>>([]);
  const [dialog, setDialog] = useState<GalleryDialog | null>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<boolean>(false);
  const [pendingOrder, setPendingOrder] = useState<Array<string> | null>(null);
  const update = (key: string, changes: Partial<Upload>): void =>
    setUploads((current) => current.map((one) => (one.key === key ? { ...one, ...changes } : one)));
  const send = useAppMutation(
    ({ key, file }: { key: string; file: File }) =>
      uploadProductImage(companyId, product.id, file, (progress) => update(key, { progress })),
    INVALIDATES,
  );
  const reorder = useAppMutation(
    (ids: Array<string>) => reorderProductImages(companyId, product.id, ids),
    INVALIDATES,
  );
  const makeMain = useAppMutation(
    (id: string) => setMainProductImage(companyId, product.id, id),
    INVALIDATES,
  );
  const remove = useAppMutation(
    (id: string) => removeProductImage(companyId, product.id, id),
    INVALIDATES,
    () => setDialog(null),
  );

  const stored = query.data ?? [];
  const order = pendingOrder ?? stored.map((photo) => photo.id);
  const photos = order
    .map((id) => stored.find((photo) => photo.id === id))
    .filter((photo): photo is GalleryImage => photo !== undefined);
  const busy = uploads.some((one) => one.state === 'waiting' || one.state === 'sending');
  const labelOf = new Map(product.variants.map((variant) => [variant.id, variantLabel(variant, t)]));

  const add = async (files: Array<File>): Promise<void> => {
    const batch: Array<{ upload: Upload; file: File }> = files.map((file, index) => ({
      upload: {
        key: `${Date.now()}-${index}-${file.name}`,
        name: file.name,
        progress: 0,
        state: file.size > MAX_PHOTO_BYTES ? 'refused' : 'waiting',
        error:
          file.size > MAX_PHOTO_BYTES ? t('gallery.tooLarge', { max: MAX_PHOTO_BYTES / 1024 / 1024 }) : null,
      },
      file,
    }));

    setUploads((current) => [
      ...current.filter((one) => one.state !== 'done'),
      ...batch.map((one) => one.upload),
    ]);

    // One after another: the gallery's order is the order the files were chosen in.
    for (const { upload, file } of batch.filter((one) => one.upload.state === 'waiting')) {
      update(upload.key, { state: 'sending' });

      try {
        await send.mutateAsync({ key: upload.key, file });
        update(upload.key, { state: 'done', progress: 1 });
      } catch (error) {
        update(upload.key, {
          state: 'refused',
          error: error instanceof Error ? error.message : t('error.requestFailed'),
        });
      }
    }
  };

  const commitOrder = (ids: Array<string>): void => {
    if (ids.join() === stored.map((photo) => photo.id).join()) {
      return;
    }

    setPendingOrder(ids);
    reorder.mutate(ids, { onSettled: () => setPendingOrder(null) });
  };

  const onDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    setOver(false);

    if (event.dataTransfer.files.length > 0 && canManage) {
      void add([...event.dataTransfer.files]);
    }
  };

  const viewing = dialog?.kind === 'view' ? photos[dialog.index] : undefined;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h6">{t('gallery.title')}</Typography>
          <Chip size="small" label={t('gallery.count', { count: stored.length, max: MAX_GALLERY_PHOTOS })} />
        </Stack>
        {canManage ? (
          <Button
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            onClick={() => input.current?.click()}
            disabled={stored.length >= MAX_GALLERY_PHOTOS}
          >
            {t('gallery.add')}
          </Button>
        ) : null}
      </Stack>
      <input
        ref={input}
        type="file"
        accept={PHOTO_ACCEPT}
        multiple
        hidden
        data-testid="gallery-file-input"
        onChange={(event) => {
          void add([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
      <Paper
        variant="outlined"
        onDragOver={(event: DragEvent<HTMLElement>) => {
          if (canManage && event.dataTransfer.types.includes('Files')) {
            event.preventDefault();
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        sx={{
          p: 2,
          borderStyle: over ? 'dashed' : 'solid',
          borderColor: over ? 'primary.main' : 'divider',
          bgcolor: over ? 'action.hover' : undefined,
        }}
      >
        <ErrorNotice
          error={query.error ?? reorder.error ?? makeMain.error}
          onRetry={() => void query.refetch()}
        />
        {uploads.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {uploads.map((upload) => (
              <Box key={upload.key}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" noWrap>
                    {upload.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={upload.state === 'refused' ? 'error' : 'text.secondary'}
                  >
                    {t(`gallery.upload.${upload.state}`)}
                  </Typography>
                </Stack>
                {upload.state === 'refused' ? (
                  <Alert severity="error" variant="outlined" sx={{ py: 0, mt: 0.5 }}>
                    {upload.error}
                  </Alert>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    value={Math.round(upload.progress * 100)}
                    color={upload.state === 'done' ? 'success' : 'primary'}
                    aria-label={upload.name}
                  />
                )}
              </Box>
            ))}
            {!busy ? (
              <Box>
                <Button size="small" onClick={() => setUploads([])}>
                  {t('gallery.clearUploads')}
                </Button>
              </Box>
            ) : null}
          </Stack>
        ) : null}

        {query.isLoading ? (
          <Skeleton variant="rounded" height={160} />
        ) : photos.length === 0 ? (
          <EmptyState
            title={t('gallery.empty')}
            detail={canManage ? t('gallery.emptyManage') : t('gallery.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddPhotoAlternateOutlinedIcon />} onClick={() => input.current?.click()}>
                  {t('gallery.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {canManage ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('gallery.dropHint')}
              </Typography>
            ) : null}
            <Box
              role="list"
              aria-label={t('gallery.title')}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
                gap: 1.5,
              }}
            >
              {photos.map((photo, index) => (
                <Paper
                  key={photo.id}
                  role="listitem"
                  variant="outlined"
                  draggable={canManage}
                  data-testid="gallery-photo"
                  onDragStart={(event: DragEvent<HTMLElement>) => {
                    setDragged(photo.id);
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', photo.id);
                  }}
                  onDragEnd={() => setDragged(null)}
                  onDragOver={(event: DragEvent<HTMLElement>) => {
                    if (dragged !== null) {
                      event.preventDefault();
                    }
                  }}
                  onDrop={(event: DragEvent<HTMLElement>) => {
                    if (dragged === null) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    commitOrder(moveTo(order, dragged, index));
                    setDragged(null);
                  }}
                  sx={{
                    overflow: 'hidden',
                    opacity: dragged === photo.id ? 0.4 : 1,
                    borderColor: photo.main ? 'warning.main' : 'divider',
                    cursor: canManage ? 'grab' : 'default',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setDialog({ kind: 'view', index })}
                    aria-label={t('gallery.view', { position: index + 1 })}
                    sx={{
                      display: 'block',
                      width: '100%',
                      p: 0,
                      border: 0,
                      bgcolor: 'action.hover',
                      cursor: 'zoom-in',
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.urls.small.webp}
                      alt={photo.alt[languages[0]] ?? ''}
                      loading="lazy"
                      draggable={false}
                      sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box sx={{ px: 1, pt: 0.75 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      {photo.main ? <Chip size="small" color="warning" label={t('gallery.main')} /> : null}
                      {languages.some((code) => photo.alt[code] === null) ? (
                        <Chip size="small" variant="outlined" label={t('gallery.altMissing')} />
                      ) : null}
                      {photo.variantIds.map((id) => (
                        <Chip key={id} size="small" variant="outlined" label={labelOf.get(id) ?? id} />
                      ))}
                    </Stack>
                  </Box>
                  {canManage ? (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5, pb: 0.5 }}>
                      <Box>
                        <Tooltip title={t('gallery.moveEarlier')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              aria-label={t('gallery.moveEarlier')}
                              onClick={() => commitOrder(moveBy(order, photo.id, -1))}
                            >
                              <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('gallery.moveLater')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === photos.length - 1}
                              aria-label={t('gallery.moveLater')}
                              onClick={() => commitOrder(moveBy(order, photo.id, 1))}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box>
                        <Tooltip title={photo.main ? t('gallery.main') : t('gallery.makeMain')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={photo.main}
                              aria-label={photo.main ? t('gallery.main') : t('gallery.makeMain')}
                              onClick={() => makeMain.mutate(photo.id)}
                            >
                              {photo.main ? (
                                <StarIcon fontSize="small" color="warning" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('gallery.edit')}>
                          <IconButton
                            size="small"
                            aria-label={t('gallery.edit')}
                            onClick={() => setDialog({ kind: 'edit', photo })}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.delete')}
                            onClick={() => setDialog({ kind: 'remove', photo })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  ) : null}
                </Paper>
              ))}
            </Box>
          </>
        )}
      </Paper>

      {dialog?.kind === 'edit' ? (
        <GalleryImageDialog
          companyId={companyId}
          product={product}
          photo={dialog.photo}
          languages={languages}
          available={available}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <ConfirmDialog
          title={t('gallery.removeTitle')}
          message={
            dialog.photo.main && photos.length > 1
              ? t('gallery.removeMainMessage')
              : t('gallery.removeMessage')
          }
          confirmLabel={t('common.delete')}
          onConfirm={() => remove.mutate(dialog.photo.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
      {dialog?.kind === 'view' && viewing ? (
        <Dialog open onClose={() => setDialog(null)} maxWidth="lg" aria-label={t('gallery.preview')}>
          <DialogContent sx={{ p: 1, position: 'relative' }}>
            <IconButton
              aria-label={t('common.close')}
              onClick={() => setDialog(null)}
              sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={viewing.urls.large.webp}
              alt={viewing.alt[languages[0]] ?? ''}
              sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh', mx: 'auto' }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
              <IconButton
                aria-label={t('gallery.previous')}
                disabled={dialog.index === 0}
                onClick={() => setDialog({ kind: 'view', index: dialog.index - 1 })}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {viewing.alt[languages[0]] ?? t('gallery.altMissing')} ·{' '}
                {t('gallery.position', { position: dialog.index + 1, count: photos.length })} ·{' '}
                {viewing.width}×{viewing.height}
              </Typography>
              <IconButton
                aria-label={t('gallery.next')}
                disabled={dialog.index === photos.length - 1}
                onClick={() => setDialog({ kind: 'view', index: dialog.index + 1 })}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
};
